import { StyledSelect } from "@ando/ui";
import * as React from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Globe,
  Loader2,
  RefreshCw,
  Server,
  XCircle,
  Zap,
} from "lucide-react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import type { MessageDescriptor } from "@lingui/core";

import { Badge, Button, cn, useToast } from "@ando/ui";
import type { ProvisionTier } from "@ando/core";

import {
  useAdminOrgProvision,
  useDeprovisionAdminOrg,
  useExecuteAdminOrgProvision,
  useRequestAdminOrgProvision,
  useRetryAdminOrgProvision,
} from "@/features/admin/hooks/useAdminOrg";

import { ConfirmDialog, LoadingRow } from "./_shared";

/**
 * Infrastructure tab — tenant provisioning lifecycle (shared → request
 * → execute → active / failed → retry → deprovision).
 *
 * v1 reference: ``OrgInfrastructureTab.tsx`` (326 LoC). 1:1 with v1 in
 * terms of capabilities; tokens are mapped to v2 brand palette.
 */

export interface OrgInfrastructureTabProps {
  orgId: string;
}

const STEPS = [
  "requested",
  "validating",
  "provisioning_infra",
  "provisioning_services",
  "configuring",
  "health_checking",
  "active",
] as const;

const REGIONS: ReadonlyArray<{
  v: string;
  label: MessageDescriptor;
}> = [
  { v: "europe-west1", label: msg`Europe West 1 (Belgium)` },
  { v: "europe-west3", label: msg`Europe West 3 (Frankfurt)` },
  { v: "europe-west4", label: msg`Europe West 4 (Netherlands)` },
  { v: "us-central1", label: msg`US Central 1 (Iowa)` },
];

export function OrgInfrastructureTab({
  orgId,
}: OrgInfrastructureTabProps): React.ReactElement {
  const { toast } = useToast();
  const { i18n } = useLingui();
  const provisionQuery = useAdminOrgProvision(orgId);
  const requestProvision = useRequestAdminOrgProvision(orgId);
  const executeProvision = useExecuteAdminOrgProvision(orgId);
  const retryProvision = useRetryAdminOrgProvision(orgId);
  const deprovision = useDeprovisionAdminOrg(orgId);

  const [tier, setTier] = React.useState<ProvisionTier>("dedicated");
  const [region, setRegion] = React.useState("europe-west1");
  const [confirmDeprov, setConfirmDeprov] = React.useState(false);

  const handleRequest = (): void => {
    requestProvision.mutate(
      { tier, region },
      {
        onSuccess: () =>
          toast.success(i18n._(msg`Provisioning richiesto`), {
            description: i18n._(msg`${tier} in ${region}.`),
          }),
        onError: (err) =>
          toast.error(i18n._(msg`Provisioning`), {
            description:
              err instanceof Error
                ? err.message
                : i18n._(msg`Errore inatteso`),
          }),
      },
    );
  };

  const handleExecute = (): void => {
    executeProvision.mutate(undefined, {
      onSuccess: () =>
        toast.success(i18n._(msg`Provisioning avviato`), {
          description: i18n._(msg`Deploy infrastruttura partito.`),
        }),
      onError: (err) =>
        toast.error(i18n._(msg`Provisioning`), {
          description:
            err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
        }),
    });
  };

  const handleRetry = (): void => {
    retryProvision.mutate(undefined, {
      onSuccess: () =>
        toast.success(i18n._(msg`Retry avviato`), {
          description: i18n._(msg`Ripreso dall'ultimo checkpoint.`),
        }),
      onError: (err) =>
        toast.error(i18n._(msg`Retry`), {
          description:
            err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
        }),
    });
  };

  const handleDeprovisionConfirmed = (): void => {
    deprovision.mutate(undefined, {
      onSuccess: () => {
        toast.success(i18n._(msg`Deprovisionata`), {
          description: i18n._(msg`Infrastruttura rilasciata.`),
        });
        setConfirmDeprov(false);
      },
      onError: (err) =>
        toast.error(i18n._(msg`Deprovision`), {
          description:
            err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
        }),
    });
  };

  if (provisionQuery.isLoading) {
    return <LoadingRow label={i18n._(msg`Caricamento infrastruttura…`)} />;
  }

  const provision = provisionQuery.data;

  if (!provision) {
    return (
      <section
        aria-labelledby="org-infra-shared"
        className="rounded-lg border border-hairline bg-card/55 p-5"
      >
        <div className="mb-3 flex items-center gap-3">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-ando-orange-soft/40 text-accent">
            <Server size={16} aria-hidden />
          </div>
          <div>
            <h2
              id="org-infra-shared"
              className="font-semibold text-[13px] text-ink-1"
            >
              <Trans>Infrastruttura condivisa</Trans>
            </h2>
            <p className="text-[12px] text-ink-3">
              <Trans>Multi-tenant con row-level security.</Trans>
            </p>
          </div>
        </div>
        <Badge variant="outline" className="text-[10px]">
          <Globe size={11} aria-hidden className="mr-1" />
          shared
        </Badge>
        <p className="mt-3 text-[12.5px] text-ink-2">
          <Trans>
            Passa a infrastruttura dedicata per database, vector store e cache
            isolati per questa org.
          </Trans>
        </p>
        <div className="mt-4 grid max-w-md grid-cols-2 gap-3">
          <label className="block text-[12px] text-ink-2">
            <Trans>Tier</Trans>
            <StyledSelect
              value={tier}
              onChange={(e) => setTier(e.target.value as ProvisionTier)}
              aria-label={i18n._(msg`Tier provisioning`)}
              className="mt-1 h-8 w-full rounded-md border border-hairline bg-card px-2 text-[12.5px] text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <option value="dedicated">
                {i18n._(msg`Dedicato (namespace K8s)`)}
              </option>
              <option value="isolated">
                {i18n._(msg`Isolato (progetto GCP)`)}
              </option>
            </StyledSelect>
          </label>
          <label className="block text-[12px] text-ink-2">
            <Trans>Regione</Trans>
            <StyledSelect
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              aria-label={i18n._(msg`Regione provisioning`)}
              className="mt-1 h-8 w-full rounded-md border border-hairline bg-card px-2 text-[12.5px] text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {REGIONS.map((r) => (
                <option key={r.v} value={r.v}>
                  {i18n._(r.label)}
                </option>
              ))}
            </StyledSelect>
          </label>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={handleRequest}
          disabled={requestProvision.isPending}
          className="mt-3"
        >
          {requestProvision.isPending ? (
            <Loader2 size={13} aria-hidden className="mr-1.5 animate-spin" />
          ) : (
            <Server size={13} aria-hidden className="mr-1.5" />
          )}
          <Trans>Richiedi provisioning</Trans>
        </Button>
      </section>
    );
  }

  const currentIdx = STEPS.indexOf(
    provision.status as (typeof STEPS)[number],
  );

  return (
    <div className="flex flex-col gap-4">
      <section
        aria-labelledby="org-infra-status"
        className="rounded-lg border border-hairline bg-card/55 p-5"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                "inline-flex h-9 w-9 items-center justify-center rounded-md",
                provision.status === "active"
                  ? "bg-success/15"
                  : provision.status === "failed"
                    ? "bg-destructive/15"
                    : "bg-warning/15",
              )}
            >
              {provision.status === "active" ? (
                <CheckCircle2
                  size={16}
                  aria-hidden
                  className="text-success-foreground"
                />
              ) : provision.status === "failed" ? (
                <XCircle size={16} aria-hidden className="text-destructive" />
              ) : (
                <Clock
                  size={16}
                  aria-hidden
                  className="animate-pulse text-warning-foreground"
                />
              )}
            </div>
            <div>
              <h2
                id="org-infra-status"
                className="font-semibold text-[13px] text-ink-1 capitalize"
              >
                <Trans>{provision.tier} infrastructure</Trans>
              </h2>
              <p className="text-[12px] text-ink-3">
                {provision.namespace
                  ? i18n._(msg`Namespace: ${provision.namespace}`)
                  : provision.gcp_project_id
                    ? i18n._(msg`Project: ${provision.gcp_project_id}`)
                    : i18n._(msg`Region: ${provision.region}`)}
              </p>
            </div>
          </div>
          <Badge
            variant={
              provision.status === "active"
                ? "success"
                : provision.status === "failed"
                  ? "destructive"
                  : "outline"
            }
            className="text-[10px] uppercase"
          >
            {provision.status.replace(/_/g, " ")}
          </Badge>
        </div>

        <div
          aria-label={i18n._(msg`Progresso provisioning`)}
          className="flex items-center gap-1 overflow-x-auto pb-1"
        >
          {STEPS.map((step, i) => {
            const isDone = i < currentIdx || provision.status === "active";
            const isCurrent = i === currentIdx;
            return (
              <React.Fragment key={step}>
                {i > 0 ? (
                  <div
                    aria-hidden
                    className={cn(
                      "h-0.5 w-4 shrink-0",
                      isDone ? "bg-success" : "bg-cream-deep",
                    )}
                  />
                ) : null}
                <div
                  className={cn(
                    "flex items-center gap-1.5 whitespace-nowrap rounded px-2 py-1 text-[11px]",
                    isDone
                      ? "bg-success/15 text-success-foreground"
                      : isCurrent
                        ? "bg-warning/15 font-medium text-warning-foreground"
                        : "text-ink-3",
                  )}
                >
                  {isDone ? (
                    <CheckCircle2 size={11} aria-hidden />
                  ) : isCurrent ? (
                    <Clock size={11} aria-hidden className="animate-pulse" />
                  ) : (
                    <span
                      aria-hidden
                      className="inline-block h-2.5 w-2.5 rounded-full border border-ink-3/40"
                    />
                  )}
                  {step.replace(/_/g, " ")}
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {provision.error_log ? (
          <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3">
            <div className="mb-1 flex items-center gap-2 font-medium text-[12.5px] text-destructive">
              <AlertCircle size={13} aria-hidden />
              <Trans>Errore (retry {provision.retry_count}/3)</Trans>
            </div>
            <pre className="whitespace-pre-wrap text-[11px] text-destructive/80">
              {provision.error_log}
            </pre>
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-2">
          {provision.status === "requested" ? (
            <Button
              type="button"
              size="sm"
              onClick={handleExecute}
              disabled={executeProvision.isPending}
            >
              {executeProvision.isPending ? (
                <Loader2
                  size={13}
                  aria-hidden
                  className="mr-1.5 animate-spin"
                />
              ) : (
                <Zap size={13} aria-hidden className="mr-1.5" />
              )}
              <Trans>Esegui provisioning</Trans>
            </Button>
          ) : null}
          {provision.status === "failed" ? (
            <Button
              type="button"
              size="sm"
              onClick={handleRetry}
              disabled={retryProvision.isPending}
            >
              {retryProvision.isPending ? (
                <Loader2
                  size={13}
                  aria-hidden
                  className="mr-1.5 animate-spin"
                />
              ) : (
                <RefreshCw size={13} aria-hidden className="mr-1.5" />
              )}
              <Trans>Riprova</Trans>
            </Button>
          ) : null}
          {(provision.status === "active" || provision.status === "failed") ? (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => setConfirmDeprov(true)}
            >
              <Trans>Deprovision</Trans>
            </Button>
          ) : null}
        </div>
      </section>

      {provision.status === "active" &&
      provision.infra_outputs &&
      Object.keys(provision.infra_outputs).length > 0 ? (
        <section
          aria-labelledby="org-infra-outputs"
          className="rounded-lg border border-hairline bg-card/55 p-5"
        >
          <h2
            id="org-infra-outputs"
            className="mb-3 font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
          >
            <Trans>Endpoint infrastruttura</Trans>
          </h2>
          <dl className="grid grid-cols-1 gap-2">
            {Object.entries(provision.infra_outputs).map(([k, v]) => (
              <div
                key={k}
                className="flex items-start gap-3 rounded bg-cream/40 p-2 text-[11.5px]"
              >
                <dt className="min-w-[140px] pt-0.5 font-mono text-ink-3">
                  {k}
                </dt>
                <dd className="break-all font-mono text-ink-1">{v}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}

      <ConfirmDialog
        open={confirmDeprov}
        onOpenChange={setConfirmDeprov}
        title={i18n._(msg`Deprovision infrastruttura?`)}
        description={i18n._(
          msg`Distrugge l'infrastruttura dedicata e riporta l'org alla modalità condivisa. I dati nel database condiviso restano intatti.`,
        )}
        confirmLabel={
          deprovision.isPending
            ? i18n._(msg`Deprovision…`)
            : i18n._(msg`Deprovision`)
        }
        destructive
        loading={deprovision.isPending}
        onConfirm={handleDeprovisionConfirmed}
      />
    </div>
  );
}
